import type { DashboardParameterValue } from "@t3tools/contracts";

import type { WorkbookCell, WorkbookSheet } from "../dashboardsWorkbook";
import {
  artifactColumnLabel,
  artifactTableColumnLabel,
  artifactKpiDelta,
  artifactWidgetColumns,
  fillArtifactFootnote,
} from "./artifactFormat";
import type { ArtifactWidgetState } from "./artifactResolve";
import type { ArtifactSpec } from "./artifactTypes";

type SnapshotInput = {
  spec: ArtifactSpec;
  version: number;
  exportedAt: Date;
  parameters: Readonly<Record<string, DashboardParameterValue>>;
  widgetState: (widgetId: string) => ArtifactWidgetState;
};

/** A failed, refreshing or missing widget must never become a plausible partial report. */
export function canExportArtifactExcel(
  spec: ArtifactSpec,
  widgetState: SnapshotInput["widgetState"],
): boolean {
  return (
    spec.rows.some((row) => row.widgets.some((widget) => widget.kind !== "text")) &&
    spec.rows.every((row) =>
      row.widgets.every((widget) => {
        if (widget.kind === "text") return true;
        const state = widgetState(widget.id);
        return (
          state.status === "ready" && !state.refreshing && state.line.resolved.kind === widget.kind
        );
      }),
    )
  );
}

function excelFormat(format: string | undefined): string | undefined {
  // Dashboard percentages are already 0–100, so Excel must not multiply by 100.
  switch (format) {
    case "percent":
      return '0.0"%"';
    case "integer":
      return "#,##0";
    case "decimal":
      return "#,##0.00";
    case "currency_usd":
      return '"$"#,##0.00';
    default:
      return undefined;
  }
}

/** Capture synchronously before the download awaits a lazy import or filters change. */
export function artifactExcelSheets(input: SnapshotInput): WorkbookSheet[] {
  const { spec, widgetState } = input;
  if (!canExportArtifactExcel(spec, widgetState)) {
    throw new Error("Wait for every dashboard widget to finish loading before exporting Excel.");
  }
  const summary: WorkbookCell[][] = [
    [spec.title, `Version ${input.version}`],
    ["Exported at (UTC)", input.exportedAt.toISOString()],
    ["Description", spec.description ?? ""],
    [
      "Snapshot",
      "Values reflect the selected filters and data freshness listed in Spec. Re-export the dashboard to refresh this workbook.",
    ],
    [],
    ["Metric", "Value", "Unit", "Comparison", "Notes"],
  ];
  const parameterRows: WorkbookCell[][] = [["Filter", "Selected value"]];
  for (const parameter of spec.parameters) {
    const value = input.parameters[parameter.id];
    if (value === undefined) continue;
    const label = (selected: string) =>
      parameter.kind !== "date_range" && parameter.options.from === "static"
        ? (parameter.options.options.find((option) => option.value === selected)?.label ?? selected)
        : selected;
    parameterRows.push([
      parameter.label,
      typeof value === "string"
        ? label(value)
        : "start" in value
          ? `${value.start} through ${value.end}`
          : value.map(label).join(", "),
    ]);
  }
  const notes: WorkbookCell[][] = [["Section", "Definition / source", "Computed at", "Freshness"]];
  for (const definition of spec.definitions) notes.push([definition.term, definition.text]);
  const sheets: WorkbookSheet[] = [];
  for (const row of spec.rows) {
    for (const widget of row.widgets) {
      if (widget.kind === "text") {
        notes.push([widget.title ?? "Notes", widget.body]);
        continue;
      }
      const state = widgetState(widget.id);
      if (state.status !== "ready") throw new Error("The dashboard is still loading");
      const { line } = state;
      const { resolved } = line;
      const columns = artifactWidgetColumns(spec, widget, line.provenance);
      const column = (name: string) => columns.get(name);
      const label = (name: string) => artifactColumnLabel(column(name), name);
      const format = (name: string) => excelFormat(column(name)?.format);
      notes.push([
        widget.title,
        line.provenance
          .map((source) =>
            source.kind === "ad_hoc"
              ? source.definition
              : `${source.metricId}: ${source.description}`,
          )
          .join("\n"),
        line.freshness?.computedAt ?? null,
        line.freshness?.status ?? "Unknown",
      ]);
      if ("description" in widget && widget.description)
        notes.push([widget.title, widget.description]);

      if (resolved.kind === "kpi" && widget.kind === "kpi") {
        summary.push([
          widget.title,
          resolved.suppressed ? null : resolved.value.value,
          column(widget.value)?.format ?? "",
          resolved.suppressed ? null : (artifactKpiDelta(widget, resolved, columns)?.text ?? null),
          resolved.suppressed
            ? "Suppressed"
            : widget.footnote
              ? fillArtifactFootnote(widget.footnote, resolved, columns)
              : null,
        ]);
      } else if (resolved.kind === "table" && widget.kind === "table") {
        const suppressed = new Set(
          resolved.suppressed?.map((cell) => `${cell.row}:${cell.column}`),
        );
        sheets.push({
          name: widget.title,
          table: true,
          formats: resolved.columns.map(format),
          rows: [
            resolved.columns.map((name) =>
              artifactTableColumnLabel(
                widget.columns.find((c) => c.column === name) ?? { column: name },
                resolved,
                columns,
              ),
            ),
            ...resolved.rows.map((cells, y) =>
              cells.map((value, x) => (suppressed.has(`${y}:${x}`) ? null : value)),
            ),
          ],
        });
      } else if (resolved.kind === "chart" && widget.kind === "chart") {
        const rows: WorkbookCell[][] = [
          [
            label(resolved.x.column),
            ...resolved.series.map((series) =>
              series.group !== undefined
                ? String(series.group)
                : (series.label ?? label(series.column)),
            ),
          ],
        ];
        resolved.x.values.forEach((x, i) =>
          rows.push([
            x,
            ...resolved.series.map((series) =>
              series.suppressed?.includes(i) ? null : (series.values[i] ?? null),
            ),
          ]),
        );
        sheets.push({
          name: widget.title,
          table: true,
          formats: [undefined, ...resolved.series.map((series) => format(series.column))],
          rows,
          // Keep unusual charts as data tables rather than alter their axes or geometry.
          ...(widget.chart !== "area" &&
          widget.orientation !== "horizontal" &&
          resolved.series.every((s) => s.axis === "left")
            ? {
                chart: {
                  kind: widget.chart,
                  title: widget.title,
                  series: resolved.series.map((series, i) => ({
                    column: i + 1,
                    kind: series.type,
                  })),
                },
              }
            : {}),
        });
      }
    }
  }
  return [
    { name: "Dashboard", rows: summary },
    { name: "Filters", rows: parameterRows, table: true },
    ...sheets,
    { name: "Spec", rows: notes, table: true },
  ];
}
